import { IOmniaShell, SharedFieldsService } from "@omnia/types"
import { useCallback, useMemo, useState } from "react"
import { Button } from "../react/buttons/Button"
import { DialogContentOptions } from "../react/dialog"
import { DialogBody } from "../react/DialogBody"
import { DialogBodyIntro } from "../react/DialogBodyIntro"
import { DialogFooter, DialogFooterButtons } from "../react/DialogFooter"
import { DialogHeader } from "../react/DialogHeader"
import { Msg } from "../react/Msg"
import { AngularReactDialogContext } from "../react/reactAngularDialog"
import CroppingSelectionGrid from "./CroppingSelectionGrid"

export function showManagePartnerCroppingsDialog(shell: IOmniaShell, args: ManagePartnerCroppingsDialogArgs) {
    return shell.showComponentAsDialog(
        {
            component: DialogWrapper,
            inject: {
                fieldSvc: "OmniaFieldsService",
            },
        },
        args,
        { size: "xxlarge" }
    )
}

type ManagePartnerCroppingsDialogArgs = Omit<ManagePartnerCroppingsDialogProps, "close" | "fieldSvc">

function DialogWrapper({
    close,
    dependencies,
    arguments: args,
}: AngularReactDialogContext<
    ManagePartnerCroppingsDialogArgs,
    { fieldSvc: SharedFieldsService },
    ManagePartnerCroppingsDialogResult
>) {
    return <ManagePartnerCroppingsDialog close={close} {...dependencies} {...args} />
}

interface ManagePartnerCroppingsDialogResult {
    enabledCroppingIds: number[]
}

interface ManagePartnerCroppingsDialogProps extends DialogContentOptions<ManagePartnerCroppingsDialogResult | null> {
    partnerName: string
    fieldSvc: SharedFieldsService
    initialEnabledCroppingIds: number[]
}

function ManagePartnerCroppingsDialog({
    close,
    partnerName,
    fieldSvc,
    initialEnabledCroppingIds,
}: ManagePartnerCroppingsDialogProps) {
    const [value, setValue] = useState<number[]>(initialEnabledCroppingIds)

    const handleClose = useCallback(() => {
        close(null)
    }, [close])

    const handleCommit = useCallback(() => {
        close({ enabledCroppingIds: value })
    }, [close, value])

    const hasChanges = useMemo(
        () => Boolean(new Set(value).symmetricDifference(new Set(initialEnabledCroppingIds)).size),
        [value, initialEnabledCroppingIds]
    )

    const handleReset = useCallback(() => {
        setValue(initialEnabledCroppingIds)
    }, [])

    const croppingOptions = useMemo(() => fieldSvc.cropping.filter((x) => x.currentYear), [fieldSvc.cropping])

    return (
        <div className="omnia-manage-partner-croppings-dialog">
            <DialogHeader icon="fas fa-webhook">
                <Msg>Manage Croppings for Partner</Msg>
            </DialogHeader>
            <DialogBody padding={false}>
                <DialogBodyIntro>
                    <p>
                        <Msg
                            text="Manage which croppings (from {{year}}) are accessible to {{partnerName}}:"
                            args={{ year: fieldSvc.yearId, partnerName }}
                        />
                    </p>
                </DialogBodyIntro>
                <CroppingSelectionGrid options={croppingOptions} value={value} onChange={setValue} />
            </DialogBody>
            <DialogFooter>
                <DialogFooterButtons
                    onClose={handleClose}
                    onCommit={handleCommit}
                    hasChanges={hasChanges}
                    canCommit={hasChanges}
                >
                    {hasChanges ? (
                        <Button disabled={!hasChanges} iconClassName="fas fa-undo" onClick={handleReset}>
                            Reset
                        </Button>
                    ) : null}
                </DialogFooterButtons>
            </DialogFooter>
        </div>
    )
}
